import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, del } from '../../../api/client';
import { ORDER_REMINDERS_QUERY_KEY } from '../../../api/orderApi';
import { useAuthStore } from '../../../stores/authStore';
import { daysFromTodayString } from '../../../utils/dateUtils';

interface OrderNote {
  id: string;
  content: string;
  noteType: string;
  remindAt: string | null;
  createdAt: string;
}

interface Props {
  orderId: string;
  orderStatus?: string;
  /** 建单方：备注只有订单归属方能写，客户方读得到但写不进、也删不掉（后端 assertOrderWritable 同源） */
  isOwner: boolean;
  /** 后端投影的 canEditOrder（建单方 + 档期在途，或审核被拒的豁免单）：决定备注能否删除 */
  canEdit: boolean;
}

const NOTE_TYPES = [
  { key: '备注',  icon: 'ri-sticky-note-line',   color: 'bg-gray-100 text-gray-500'   },
  { key: '跟进',  icon: 'ri-user-follow-line',    color: 'bg-sky-50 text-sky-500'      },
  { key: '电话',  icon: 'ri-phone-line',          color: 'bg-teal-50 text-teal-500'    },
  { key: '待办',  icon: 'ri-checkbox-line',       color: 'bg-amber-50 text-amber-500'  },
  { key: '重要',  icon: 'ri-error-warning-line',  color: 'bg-rose-50 text-rose-500'    },
];

/** 与后端 `CreateOrderNoteDto.content` 的 @MaxLength 同源，超出即 400 */
const NOTE_CONTENT_MAX_LENGTH = 500;

function getNoteStyle(type: string) {
  return NOTE_TYPES.find(t => t.key === type) ?? NOTE_TYPES[0];
}

function fmtRelativeTime(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return '刚刚';
  if (diffMins < 60) return `${diffMins}分钟前`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}小时前`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}天前`;
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getRemindStatus(remindAt: string | null): 'overdue' | 'today' | 'upcoming' | null {
  if (!remindAt) return null;
  const today = todayStr();
  if (remindAt < today) return 'overdue';
  if (remindAt === today) return 'today';
  return 'upcoming';
}

export default function OrderNotes({ orderId, orderStatus, isOwner, canEdit }: Props) {
  const { profile } = useAuthStore();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [selectedType, setSelectedType] = useState('备注');
  const [remindAt, setRemindAt] = useState('');
  const [showRemindPicker, setShowRemindPicker] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  // 使用 useQuery 获取订单备注（首次加载期间 data 为 undefined，需直接给默认值避免崩溃）
  const { data: notes = [], isLoading: loading } = useQuery<OrderNote[]>({
    queryKey: ['orderNotes', orderId],
    queryFn: async () => {
      const data = await get<OrderNote[]>(`/order-notes/order/${orderId}`);
      return data ?? [];
    },
  });

  /**
   * 备注写入判据，与后端 order-notes.service 同源：
   * 只有建单方（`Order.user_id`）能写；在途单随便加，终态单（已完成/已归档）只允许补一条，
   * 审核被拒的单靠 canEdit 豁免照常可写。
   */
  const isTerminalAppendable = orderStatus === '已完成' || orderStatus === '已归档';
  const canAddNote = isOwner && (canEdit || (isTerminalAppendable && notes.length === 0));
  /** 删除备注不做「终态补一条」的豁免，一律按 canEdit 收口 */
  const canDeleteNote = isOwner && canEdit;

  // 使用 useMutation 添加订单备注
  const addNoteMutation = useMutation({
    mutationFn: async (noteData: { content: string; noteType: string; remindAt: string | null }) => {
      return post<OrderNote>(`/order-notes/order/${orderId}`, noteData);
    },
    onSuccess: () => {
      // 刷新订单备注查询
      queryClient.invalidateQueries({ queryKey: ['orderNotes', orderId] });
      // 带提醒时间的备注会同时出现在首页与订单页的提醒面板，写操作必须一并通知它们
      queryClient.invalidateQueries({ queryKey: ORDER_REMINDERS_QUERY_KEY });
      setContent('');
      setRemindAt('');
      setShowRemindPicker(false);
      setShowForm(false);
      setErrorMsg(null);
    },
    onError: (error) => {
      setErrorMsg(error instanceof Error ? error.message : '添加备注失败');
    },
  });

  const handleAdd = () => {
    if (!content.trim() || !profile?.id) return;
    setErrorMsg(null);
    addNoteMutation.mutate({
      content: content.trim(),
      noteType: selectedType,
      remindAt: remindAt || null,
    });
  };

  // 使用 useMutation 删除订单备注
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      return del(`/order-notes/order/${orderId}/${noteId}`);
    },
    onSuccess: () => {
      // 刷新订单备注查询
      queryClient.invalidateQueries({ queryKey: ['orderNotes', orderId] });
      // 删掉带提醒的备注后，两处提醒面板不能再摆着那条已经不存在的内容
      queryClient.invalidateQueries({ queryKey: ORDER_REMINDERS_QUERY_KEY });
      if (expandedId === deleteNoteMutation.variables) setExpandedId(null);
      setErrorMsg(null);
    },
    onError: (error) => {
      setErrorMsg(error instanceof Error ? error.message : '删除备注失败');
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const handleDelete = (id: string) => {
    setDeletingId(id);
    deleteNoteMutation.mutate(id);
  };

  const handleShowForm = () => {
    setShowForm(true);
    setErrorMsg(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const REMIND_SHORTCUTS = [
    { label: '明天', getValue: () => daysFromTodayString(1) },
    { label: '3天后', getValue: () => daysFromTodayString(3) },
    { label: '1周后', getValue: () => daysFromTodayString(7) },
  ];

  return (
    <div>
      {/* 标题 + 添加按钮 */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          备注 / 跟进
          {notes.length > 0 && (
            <span className="text-[10px] bg-gray-200 text-gray-500 rounded-full px-1.5 py-0.5 font-medium">{notes.length}</span>
          )}
        </p>
        {!showForm && canAddNote && (
          <button onClick={handleShowForm}
            className="flex items-center gap-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </div>
            添加备注
          </button>
        )}
      </div>

      {/* 新增/删除失败原因（服务端校验与权限拒绝都会落到这里） */}
      {errorMsg && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-600">
          <i className="ri-error-warning-line mt-0.5 shrink-0" />
          <span className="flex-1">{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)}
            className="w-4 h-4 flex items-center justify-center text-rose-300 hover:text-rose-500 cursor-pointer shrink-0">
            <i className="ri-close-line text-xs" />
          </button>
        </div>
      )}

      {/* 添加表单 */}
      {showForm && canAddNote && (
        <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-3">
          {/* 类型选择器 */}
          <div className="flex gap-1.5 flex-wrap">
            {NOTE_TYPES.map(t => (
              <button key={t.key} onClick={() => setSelectedType(t.key)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-all border whitespace-nowrap ${
                  selectedType === t.key
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                }`}>
                <i className={`${t.icon} text-xs`} />
                {t.key}
              </button>
            ))}
          </div>

          {/* 文本输入 */}
          <textarea
            ref={textareaRef}
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="输入备注内容，如跟进情况、电话沟通记录、待办事项..."
            rows={3}
            maxLength={NOTE_CONTENT_MAX_LENGTH}
            className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 resize-none transition-all placeholder:text-gray-400"
          />

          {/* 提醒日期区块 */}
          <div className="border-t border-teal-100 pt-2.5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-alarm-line text-amber-500 text-xs" />
                设置提醒日期（选填）
              </span>
              <button onClick={() => setShowRemindPicker(v => !v)}
                className={`text-xs cursor-pointer transition-colors ${showRemindPicker ? 'text-teal-600' : 'text-gray-400 hover:text-teal-500'}`}>
                {showRemindPicker ? '收起' : '展开'}
              </button>
            </div>
            {showRemindPicker && (
              <div className="space-y-2">
                <div className="flex gap-1.5">
                  {REMIND_SHORTCUTS.map(s => (
                    <button key={s.label} onClick={() => setRemindAt(s.getValue())}
                      className={`px-2.5 py-1 rounded-full text-xs border cursor-pointer transition-all whitespace-nowrap ${
                        remindAt === s.getValue()
                          ? 'bg-amber-400 text-white border-amber-400'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="date" value={remindAt} min={todayStr()}
                    onChange={e => setRemindAt(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300 text-gray-700 transition-all" />
                  {remindAt && (
                    <button onClick={() => setRemindAt('')}
                      className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer">
                      <i className="ri-close-line text-sm" />
                    </button>
                  )}
                </div>
                {remindAt && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <i className="ri-alarm-line text-xs" />
                    将在 {remindAt} 提醒你跟进此备注
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-1">
            <span className={`text-xs ${content.length >= NOTE_CONTENT_MAX_LENGTH ? 'text-rose-500' : 'text-gray-400'}`}>
              {content.length} / {NOTE_CONTENT_MAX_LENGTH}
            </span>
            <div className="flex gap-2">
              <button onClick={() => { setShowForm(false); setContent(''); setRemindAt(''); setShowRemindPicker(false); setErrorMsg(null); }}
                disabled={addNoteMutation.isPending}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors bg-white">
                取消
              </button>
              <button onClick={handleAdd} disabled={addNoteMutation.isPending || !content.trim()}
                className="px-4 py-1.5 text-xs bg-teal-500 text-white rounded-lg hover:bg-teal-600 cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors flex items-center gap-1">
                {addNoteMutation.isPending ? <i className="ri-loader-4-line animate-spin text-xs" /> : <i className="ri-check-line text-xs" />}
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 时间线列表 */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="w-6 h-6 rounded-full bg-gray-100 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-3 bg-gray-100 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mb-2">
            <i className="ri-chat-3-line text-gray-300 text-lg" />
          </div>
          <p className="text-xs text-gray-400">
            {canAddNote ? '暂无备注，点击「添加备注」记录跟进情况' : '暂无备注'}
          </p>
        </div>
      ) : (
        <div className="relative">
          {notes.length > 1 && <div className="absolute left-[11px] top-6 bottom-4 w-px bg-gray-100" />}
          <div className="space-y-4">
            {notes.map(note => {
              const style = getNoteStyle(note.noteType);
              const isExpanded = expandedId === note.id;
              const isLong = note.content.length > 80;
              const remindStatus = getRemindStatus(note.remindAt);
              return (
                <div key={note.id} className="flex gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 z-10 ${style.color}`}>
                    <i className={`${style.icon} text-xs`} />
                  </div>
                  <div className="flex-1 min-w-0 bg-gray-50 rounded-xl p-3 group">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${style.color}`}>
                        {note.noteType}
                      </span>
                      {/* 提醒日期徽章 */}
                      {note.remindAt && (
                        <span className={`flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${
                          remindStatus === 'overdue' ? 'bg-rose-50 text-rose-500 border-rose-200' :
                          remindStatus === 'today'   ? 'bg-amber-50 text-amber-600 border-amber-200' :
                          'bg-sky-50 text-sky-500 border-sky-200'
                        }`}>
                          <i className="ri-alarm-line text-[10px]" />
                          {remindStatus === 'overdue' ? '已逾期 ' : remindStatus === 'today' ? '今日 ' : ''}
                          {note.remindAt.slice(5)}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 flex-1 truncate">{fmtRelativeTime(note.createdAt)}</span>
                      {canDeleteNote && (
                        <button onClick={() => handleDelete(note.id)} disabled={deletingId === note.id}
                          className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center text-gray-300 hover:text-rose-400 cursor-pointer transition-all shrink-0">
                          {deletingId === note.id
                            ? <i className="ri-loader-4-line animate-spin text-xs" />
                            : <i className="ri-close-line text-sm" />}
                        </button>
                      )}
                    </div>
                    <p className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
                      {note.content}
                    </p>
                    {isLong && (
                      <button onClick={() => setExpandedId(isExpanded ? null : note.id)}
                        className="mt-1 text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors">
                        {isExpanded ? '收起' : '展开全文'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
